import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';

import { OptionsObject, SnackbarMessage, useSnackbar } from 'notistack';
import { useCollectionDataOnce, useDocument } from 'react-firebase-hooks/firestore';
import { collection, doc, limit, query, where } from 'firebase/firestore';
import firebaseApp from '../../../firebase-app';
import { AppConfigData, APP_TYPE } from '../../../types/app-config';
import { Poll } from '../../scenes/polls/types';
import { useStringsContext } from '../../../contexts/strings';
import { FS_PATH_POLLS } from '../../../constants';
import { ROLES } from '../../../types';
import { Field, FIELD_TYPES } from '../../../types';
import { useUserAuth } from '../../../contexts/auth';
import SnackbarMenuAction from './snackbar-action-menu';
import { createInitialUIschema } from './UI-schema';
import { DATA_LOCATION, TeamData } from './types';
import {
  databaseUpdateOccurredWith,
  dataTypeConverter,
  applyNewFieldValue,
  isAllFieldsValid,
} from './utils';
import { getAppConfigPath, getTeamDataPath } from '../../../utils/paths';
import adminApi from '../../../api/admin-api';

/** The label of the home arena info row, typos included (`Hemmaarena:`, `Hemma-arena:`). */
const HOME_ARENA_INFO_NAME = /^hemma.*arena/i;

/**
 * Whether an info row is the home arena row. We hide it because editing it has no effect: the
 * API (`toInfo` in `packages/internal-data/lib/team/format.ts`) replaces its value with the
 * arena's name, and adds the row when it is missing. The arena's name is edited under Arena.
 * The hand-typed value used to drift from it (e.g. NOR showed two different arenas).
 * Hidden rows are still saved as they are.
 */
const isHomeArenaInfo = (field: Field, team: TeamData | undefined) =>
  field.type === FIELD_TYPES.INFO_FIELDS &&
  !!field.fields?.some(
    (infoField) =>
      infoField.key.endsWith('.name') &&
      HOME_ARENA_INFO_NAME.test(String(get(team, infoField.key) ?? ''))
  );

export function useTeamData(teamAbbrv: string) {
  const { isSuperAdmin, hasTeamRole } = useUserAuth();
  const { strings } = useStringsContext();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { currentUser } = useUserAuth();

  const [isValid, setIsValid] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [UIschema, setUIschema] = useState<any[]>([]);
  const [pendingIncomingTeamData, setPendingIncomingTeamData] = useState<TeamData | null>(null);
  const [pendingIncomingAppConfig, setPendingIncomingAppConfig] = useState<AppConfigData | null>(
    null
  );

  const hasLoadedOnce = useRef(false);
  const editedFields = useRef<{ [key: string]: boolean }>({});
  const remoteTeamDataRef = useRef<TeamData>();
  const localTeamDataRef = useRef<TeamData>();
  const remoteAppConfigDataRef = useRef<AppConfigData>();
  const localAppConfigDataRef = useRef<AppConfigData>();
  const pendingIncomingTimeout = useRef<NodeJS.Timeout>();

  const [teamDoc, teamDocLoading, teamDocError] = useDocument(
    doc(firebaseApp.db, getTeamDataPath(teamAbbrv))
  );

  const [appConfigDoc, appConfigDocLoading, appConfigDocError] = useDocument(
    doc(firebaseApp.db, getAppConfigPath(teamAbbrv))
  );

  const [polls, pollDocsLoading, pollDocsError] = useCollectionDataOnce(
    query(collection(firebaseApp.db, FS_PATH_POLLS), where('abbrv', '==', teamAbbrv), limit(100))
  );

  const updateUIschema = useCallback(
    (team: TeamData | undefined, appConfig: AppConfigData | undefined) => {
      const initialUIschema = createInitialUIschema(teamAbbrv, polls as Poll[]);
      const newUIschema = initialUIschema.reduce((acc: any[], category: any) => {
        if (appConfig?.appType === APP_TYPE.CLUB || !category.isTeamDataValue) {
          category.fields = category.fields
            .filter(
              (field: Field) => appConfig?.appType === APP_TYPE.CLUB || !field.isTeamDataValue
            )
            .filter((field: Field) => !isHomeArenaInfo(field, team))
            .map((field: Field) => {
              if (field.type === FIELD_TYPES.INFO_FIELDS) {
                field.fields?.forEach((infoField: any) => {
                  const initTeamValue = get(team, infoField.key);
                  const initDataType = dataTypeConverter(initTeamValue, infoField.type);
                  infoField.value = initDataType;
                  infoField.isTeamDataValue =
                    infoField.isTeamDataValue === undefined
                      ? category.isTeamDataValue
                      : infoField.isTeamDataValue;
                  infoField.isReadOnly =
                    infoField.isReadOnly === undefined ? category.isReadOnly : infoField.isReadOnly;
                  return { ...infoField };
                });
              }

              if (field.type === FIELD_TYPES.PROMO_FIELD) {
                field.fields?.forEach((promoField: any) => {
                  const initialTeamValue = get(appConfig, promoField.key);
                  const newFieldValue = dataTypeConverter(initialTeamValue, promoField.type);
                  promoField.value = newFieldValue;
                  promoField.isTeamDataValue =
                    promoField.isTeamDataValue === undefined
                      ? category.isTeamDataValue
                      : promoField.isTeamDataValue;
                  promoField.isReadOnly =
                    promoField.isReadOnly === undefined
                      ? category.isReadOnly
                      : promoField.isReadOnly;
                  return { ...promoField };
                });
              }

              const data = category.isTeamDataValue || field.isTeamDataValue ? team : appConfig;
              const initTeamValue = get(data, field.key);
              const initDataType = dataTypeConverter(initTeamValue, field.type);

              field.value = initDataType;
              field.isTeamDataValue =
                field.isTeamDataValue === undefined
                  ? category.isTeamDataValue
                  : field.isTeamDataValue;
              field.isReadOnly =
                field.isReadOnly === undefined ? category.isReadOnly : field.isReadOnly;
              return { ...field };
            });
          acc.push({ ...category });
        }
        return acc;
      }, []);
      setUIschema(cloneDeep(newUIschema));
    },
    [polls, teamAbbrv]
  );

  const dismissIncomingChanges = useCallback(() => {
    closeSnackbar();
  }, [closeSnackbar]);

  const acceptIncomingChanges = useCallback(
    (incomingTeamData: TeamData | null, incomingAppConfigData: AppConfigData | null) => {
      if (incomingTeamData) {
        localTeamDataRef.current = cloneDeep(incomingTeamData);
        remoteTeamDataRef.current = cloneDeep(incomingTeamData);
      }

      if (incomingAppConfigData) {
        localAppConfigDataRef.current = cloneDeep(incomingAppConfigData);
        remoteAppConfigDataRef.current = cloneDeep(incomingAppConfigData);
      }

      updateUIschema(localTeamDataRef.current, localAppConfigDataRef.current);

      closeSnackbar();
      setIsDirty(false);
    },
    [closeSnackbar, updateUIschema]
  );

  const showSnackbar = useCallback(
    (incomingTeamData: TeamData | null, incomingAppConfigData: AppConfigData | null) => {
      const incomingChangesBy = incomingTeamData?.author || incomingAppConfigData?.author;
      const snackbarMessage: SnackbarMessage = strings.apps.incomingChange.replace(
        '$author',
        incomingChangesBy || strings.apps.unknown
      );

      const snackbarConfig: OptionsObject = {
        variant: 'info',
        persist: true,
        action: (
          <SnackbarMenuAction
            accept={() => dismissIncomingChanges()}
            dismiss={() => acceptIncomingChanges(incomingTeamData, incomingAppConfigData)}
          />
        ),
      };
      enqueueSnackbar(snackbarMessage, snackbarConfig);
    },
    [dismissIncomingChanges, enqueueSnackbar, acceptIncomingChanges, strings]
  );

  useEffect(() => {
    if (pendingIncomingTeamData || pendingIncomingAppConfig) {
      clearTimeout(pendingIncomingTimeout.current);

      pendingIncomingTimeout.current = setTimeout(() => {
        showSnackbar(pendingIncomingTeamData, pendingIncomingAppConfig);
        setPendingIncomingTeamData(null);
        setPendingIncomingAppConfig(null);
      }, 1000);
    }
  }, [pendingIncomingAppConfig, pendingIncomingTeamData, showSnackbar]);

  // runs when firestore gets updated (appConfig)
  useEffect(() => {
    if (hasLoadedOnce.current && !isSubmitting) {
      const incomingAppConfigData = appConfigDoc?.data() as AppConfigData;

      if (
        remoteAppConfigDataRef.current &&
        incomingAppConfigData &&
        localAppConfigDataRef.current
      ) {
        const dataLocation = databaseUpdateOccurredWith(
          incomingAppConfigData,
          localAppConfigDataRef.current,
          remoteAppConfigDataRef.current
        );

        switch (dataLocation) {
          case DATA_LOCATION.NONE:
            // no updates occurred
            break;
          case DATA_LOCATION.LOCAL:
            // local changes only, keep server ref up to date
            remoteAppConfigDataRef.current = cloneDeep(incomingAppConfigData);
            break;
          case DATA_LOCATION.SERVER:
            // server changes only, merge server changes
            localAppConfigDataRef.current = cloneDeep(incomingAppConfigData);
            remoteAppConfigDataRef.current = cloneDeep(incomingAppConfigData);
            updateUIschema(localTeamDataRef.current, localAppConfigDataRef.current);
            break;
          case DATA_LOCATION.BOTH:
            // both local and server changes, ask user what to do
            setPendingIncomingAppConfig(incomingAppConfigData);
            break;
          default:
            break;
        }
      }
    }
  }, [appConfigDoc]);

  // runs when firestore gets updated (teamData)
  useEffect(() => {
    if (hasLoadedOnce.current && !isSubmitting) {
      const incomingTeamDocData = teamDoc?.data() as TeamData;

      if (remoteTeamDataRef.current && incomingTeamDocData && localTeamDataRef.current) {
        const dataLocation = databaseUpdateOccurredWith(
          incomingTeamDocData,
          localTeamDataRef.current,
          remoteTeamDataRef.current
        );

        switch (dataLocation) {
          case DATA_LOCATION.NONE:
            // no updates occurred
            break;
          case DATA_LOCATION.LOCAL:
            // local changes only, keep server ref up to date
            remoteTeamDataRef.current = cloneDeep(incomingTeamDocData);
            break;
          case DATA_LOCATION.SERVER:
            // server changes only, merge server changes
            localTeamDataRef.current = cloneDeep(incomingTeamDocData);
            remoteTeamDataRef.current = cloneDeep(incomingTeamDocData);
            updateUIschema(localTeamDataRef.current, localAppConfigDataRef.current);
            break;
          case DATA_LOCATION.BOTH:
            // both local and server changes, ask user what to do
            setPendingIncomingTeamData(incomingTeamDocData);
            break;
          default:
            break;
        }
      }
    }
  }, [teamDoc]);

  // INITIAL - only runs once on page load
  useEffect(() => {
    if (
      !hasLoadedOnce.current &&
      !teamDocLoading &&
      !teamDocError &&
      !appConfigDocLoading &&
      !appConfigDocError &&
      !pollDocsLoading &&
      !pollDocsError
    ) {
      const incomingTeamDocData = teamDoc?.data() as TeamData;
      const incomingAppConfigData = appConfigDoc?.data() as AppConfigData;

      if (incomingTeamDocData) {
        localTeamDataRef.current = cloneDeep(incomingTeamDocData);
        remoteTeamDataRef.current = cloneDeep(incomingTeamDocData);
      }

      if (incomingAppConfigData) {
        localAppConfigDataRef.current = cloneDeep(incomingAppConfigData);
        remoteAppConfigDataRef.current = cloneDeep(incomingAppConfigData);
      }

      updateUIschema(incomingTeamDocData, incomingAppConfigData);
      hasLoadedOnce.current = true;
    }
  }, [
    teamDoc,
    appConfigDoc,
    teamDocLoading,
    teamDocError,
    appConfigDocLoading,
    appConfigDocError,
    pollDocsLoading,
    pollDocsError,
    updateUIschema,
  ]);

  const setValidation = useCallback(
    async (fieldKey: string, value: string | number | boolean, fieldValidation: any) => {
      const isCurrentFieldValid: boolean = await fieldValidation.isValid(value);
      editedFields.current[fieldKey] = isCurrentFieldValid;
      const isFieldValid = isAllFieldsValid(editedFields.current);

      setIsValid(isFieldValid);
    },
    []
  );

  const setChange = useCallback(
    (
      fieldKey: string,
      value: string | number | boolean | null,
      fieldValidation: any,
      isTeamDataValue: boolean
    ) => {
      if (fieldKey === 'Not available') {
        console.error(`The key: ${fieldKey} is not defined in the UI-schema.`);
        return;
      }

      setIsDirty(true);

      if (isTeamDataValue) {
        if (localTeamDataRef.current) {
          applyNewFieldValue(localTeamDataRef.current, fieldKey, value);
        }
      } else {
        if (localAppConfigDataRef.current) {
          applyNewFieldValue(localAppConfigDataRef.current, fieldKey, value);
        }
      }
    },
    []
  );

  const onSubmit = useCallback(
    async (event: { preventDefault: () => void }) => {
      event.preventDefault();
      if (!isValid) {
        return;
      }

      const configTeamAbbrv = localTeamDataRef.current?.abbrv;
      const configAppShortname = localAppConfigDataRef.current?.appShortname;

      if (
        (configTeamAbbrv !== undefined && teamAbbrv !== configTeamAbbrv) ||
        teamAbbrv !== configAppShortname
      ) {
        enqueueSnackbar(strings.messages.uploadError, {
          variant: 'error',
        });

        console.error('Team abbrv missmatch!', teamAbbrv, configTeamAbbrv, configAppShortname);

        firebaseApp.logEvent('team_abbrv_missmatch', {
          abbrv: teamAbbrv,
          teamDataAbbrv: configTeamAbbrv,
          appConfigDataAbbrv: configAppShortname,
        });
        return;
      }

      editedFields.current = {};
      setIsSubmitting(true);

      firebaseApp.logEvent('app_publish', {
        abbrv: teamAbbrv,
      });

      try {
        if (
          localTeamDataRef.current &&
          !isEqual(localTeamDataRef.current, remoteTeamDataRef.current)
        ) {
          const response = await adminApi.post({
            path: `/app/team-config/${teamAbbrv}`,
            auth: await currentUser?.getIdToken(),
            body: { config: localTeamDataRef.current },
          });
          const responseData = await response.json();
          if (responseData.error) {
            throw new Error(responseData.error);
          }

          // keep server ref up-to-date
          remoteTeamDataRef.current = cloneDeep(localTeamDataRef.current);

          setIsDirty(false);
        }

        if (
          localAppConfigDataRef.current &&
          !isEqual(localAppConfigDataRef.current, remoteAppConfigDataRef.current)
        ) {
          const response = await adminApi.post({
            path: `/app/app-config/${teamAbbrv}`,
            auth: await currentUser?.getIdToken(),
            body: { config: localAppConfigDataRef.current },
          });
          const responseData = await response.json();

          if (responseData.error) {
            throw new Error(responseData.error);
          }

          // keep server ref up-to-date
          remoteAppConfigDataRef.current = cloneDeep(localAppConfigDataRef.current);
          setIsDirty(false);
        }

        enqueueSnackbar(strings.messages.uploadSuccess, {
          variant: 'success',
        });
      } catch (error: any) {
        enqueueSnackbar(strings.messages.uploadError, {
          variant: 'error',
        });
        console.error(error.message, error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [enqueueSnackbar, isValid, strings, teamAbbrv]
  );

  const isReadOnly = !isSuperAdmin && hasTeamRole(teamAbbrv, ROLES.VIEWER);

  const categories = useMemo(() => {
    return UIschema.filter((category: any) => {
      return (isSuperAdmin && category.superAdminOnly) || !category.superAdminOnly;
    });
  }, [UIschema, isSuperAdmin]);

  const handleValidation = useCallback(
    async (fieldKey: string, value: string | number | boolean, fieldValidation: any) => {
      setValidation(fieldKey, value, fieldValidation);
    },
    [setValidation]
  );

  return {
    categories,
    teamDocError,
    appConfigDocError,
    teamDocLoading,
    appConfigDocLoading,
    onSubmit,
    isValid,
    isReadOnly,
    isDirty,
    isSubmitting,
    isSuperAdmin,
    setChange,
    setValidation,
    handleValidation,
  };
}
